/*
 * The emoji picker (spec 06 §4): recently used, the nine categories, a search over names,
 * keywords and shortcodes, and the skin tone every insertion uses. It is opened from a message
 * row's menu to react, and it is the one place a person browses emoji rather than typing ":". It is
 * core's so that a document's comment reacts through the same panel as a chat message (DV
 * S-12-D13); what it knows of the person — the recent row and the tone — comes from `recents.ts`,
 * which the module that keeps that history fills.
 *
 * It is a **popover**, hung from the control that opens it, and never a dialog: choosing an emoji
 * is a choice about the message beside it, so nothing is dimmed and the conversation stays readable
 * behind the panel. One component serves both doors — the composer's toolbar and the message row
 * each pass their own `trigger` and get the same panel, anchored to their own button, which is also
 * where Escape puts the focus back. A surface that opens the picker from somewhere other than the
 * trigger itself — a row choosing *Add a reaction* in its menu — owns the open state through
 * `open` / `onOpenChange`, and the panel still hangs from the button in that row's hover bar.
 *
 * The whole vocabulary arrives through `loadEmojiTable()` — a dynamic import, so neither the app
 * shell nor the conversation route carries 115 KB of emoji data that most sessions never open
 * (A32), and the panel is mounted only while it is open, so the chunk is asked for when somebody
 * reaches for an emoji and not before. Until it has arrived the panel carries the product's one
 * loading shape at the panel's own width, so nothing jumps when the grid lands.
 *
 * Keyboard shape (X-A2 D5): three composite widgets and one field, four tab stops in all. The
 * emoji are an APG grid — `grid` / `row` / `gridcell`, one stop, the arrows moving a roving
 * tabindex, Home and End on the row, PageUp and PageDown a screenful — and the category strip and
 * the tone row are single stops of their own that the arrows walk and choose as they go. The focus
 * lands in the search box when the panel opens and ArrowDown from there enters the grid, so the
 * whole panel is reachable without ever leaving the keyboard. Four hundred emoji as four hundred
 * tab stops is reachable in theory and unusable in practice, which is the point of the pattern.
 *
 * The emoji stays a `<button>` inside its cell rather than becoming the cell: the emoji is the
 * control a person operates, and every surface that names one — a test, the reaction scenario —
 * asks for a button by the emoji's name.
 */

import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
	type ReactNode,
} from "react"
import { Popover } from "../../ui/index.ts"
import { coarsePointer } from "../composer/pointer.ts"
import { t, type PlainKey } from "../i18n/index.ts"
import "./emoji.css"
import { rememberEmoji, setSkinTone, useEmojiPrefs } from "./recents.ts"
import {
	loadEmojiTable,
	MAX_SKIN_TONE,
	withSkinTone,
	type EmojiEntry,
	type EmojiGroup,
	type EmojiTable,
} from "./table.ts"

/**
 * How many buttons a grid row holds. The CSS and the arrow keys agree on it because it is read here
 * and handed to the stylesheet as `--emoji-columns`: two spellings of one number would mean the keys
 * stop following the cells a person sees.
 *
 * A finger needs a cell it can hit. Eight columns in a 330-px popover is a pitch of ~37 px, and the
 * drawn cell *is* the target here — the shared hit-area pseudo-element cannot grow it, because a
 * centred 44-px area would overlap its neighbours and a thumb 2 px inside one cell would insert the
 * emoji to its left (X-B3 D1, `ui.css`'s coarse-pointer rule). So on a coarse pointer the cells grow
 * (`emoji.css`: `--emoji-row`) and there are six of them per row. Both halves ask the same question —
 * the stylesheet's `@media (pointer: coarse)` and this number — because a device that is read with a
 * thumb and can also hover (a tablet with a trackpad) would otherwise get the taller row and keep the
 * eight narrow columns, and the cell would be under the target size in the axis a thumb misses in.
 */
const COLUMNS_FINE = 8
const COLUMNS_COARSE = 6

function gridColumns(): number {
	return coarsePointer() ? COLUMNS_COARSE : COLUMNS_FINE
}

/**
 * How many rows the grid shows at once. `emoji.css` gives the scroller exactly this many whole
 * rows, and PageUp and PageDown move by exactly this much: one fact in two places, and changing
 * either alone makes a page turn stop matching what a person sees.
 */
const ROWS_VISIBLE = 6

/** The cap on a search result — a screenful, so the grid never becomes a scroll of everything. */
const SEARCH_LIMIT = 48

/**
 * The copy for emojibase's group keys. A group the build has no key for falls back to the label
 * the data ships, so a new Unicode category appears with an English name rather than not at all.
 */
const GROUP_LABELS: Record<string, PlainKey> = {
	"smileys-emotion": "emoji.group.smileys",
	"people-body": "emoji.group.people",
	"animals-nature": "emoji.group.nature",
	"food-drink": "emoji.group.food",
	"travel-places": "emoji.group.travel",
	activities: "emoji.group.activities",
	objects: "emoji.group.objects",
	symbols: "emoji.group.symbols",
	flags: "emoji.group.flags",
}

/** One emoji per category, drawn on its tab: an icon row that needs no icon set. */
const GROUP_ICONS: Record<string, string> = {
	"smileys-emotion": "😀",
	"people-body": "👋",
	"animals-nature": "🐻",
	"food-drink": "🍎",
	"travel-places": "🚗",
	activities: "⚽",
	objects: "💡",
	symbols: "🔣",
	flags: "🏳️",
}

/** The tones, 0 to 5, as the swatch each is drawn with and the copy that names it. */
const TONES: { swatch: string; key: PlainKey }[] = [
	{ swatch: "✋", key: "emoji.tone.default" },
	{ swatch: "✋🏻", key: "emoji.tone.light" },
	{ swatch: "✋🏼", key: "emoji.tone.mediumLight" },
	{ swatch: "✋🏽", key: "emoji.tone.medium" },
	{ swatch: "✋🏾", key: "emoji.tone.mediumDark" },
	{ swatch: "✋🏿", key: "emoji.tone.dark" },
]

function groupLabel(group: EmojiGroup): string {
	const key = GROUP_LABELS[group.key]
	return key === undefined ? group.fallbackLabel : t(key)
}

/** The table once it has arrived; `undefined` while the chunk is still on its way. */
function useEmojiTable(): EmojiTable | undefined {
	const [table, setTable] = useState<EmojiTable>()
	useEffect(() => {
		let live = true
		void loadEmojiTable().then((loaded) => {
			if (live) setTable(loaded)
		})
		return () => {
			live = false
		}
	}, [])
	return table
}

/**
 * The keys the panel answers itself. Not one of them may reach what the panel is drawn over: the
 * composer's toolbar and the message row's hover bar are both roving-tabindex widgets that move on
 * an arrow, and a React portal bubbles its events through the *component* tree, not the DOM — which
 * is why ArrowRight in the picker used to move the toolbar's stop and close the panel (X-A2 D5).
 */
const OWN_KEYS = new Set([
	"ArrowLeft",
	"ArrowRight",
	"ArrowUp",
	"ArrowDown",
	"Home",
	"End",
	"PageUp",
	"PageDown",
])

/** The buttons of a composite widget, in the order a reader meets them. */
function buttonsIn(container: HTMLElement | null): HTMLButtonElement[] {
	return [...(container?.querySelectorAll<HTMLButtonElement>("button") ?? [])]
}

/** Which cell a key moves to, or null when the key is none of the grid's. */
function gridTarget(
	event: React.KeyboardEvent,
	current: number,
	count: number,
	columns: number,
): number | null {
	const rowStart = Math.floor(current / columns) * columns
	const page = columns * ROWS_VISIBLE
	switch (event.key) {
		case "ArrowLeft":
			return Math.max(0, current - 1)
		case "ArrowRight":
			return Math.min(count - 1, current + 1)
		case "ArrowUp":
			// The first row has nothing above it: the stop holds rather than sliding to the start.
			return current < columns ? current : current - columns
		case "ArrowDown":
			return Math.min(count - 1, current + columns)
		case "Home":
			return event.ctrlKey ? 0 : rowStart
		case "End":
			return event.ctrlKey ? count - 1 : Math.min(count - 1, rowStart + columns - 1)
		case "PageUp":
			return Math.max(0, current - page)
		case "PageDown":
			return Math.min(count - 1, current + page)
		default:
			return null
	}
}

/**
 * The entries in rows of `columns`, each row knowing where it starts — the shape the grid's roles
 * and its arrow keys both need, since the roving stop counts through the whole grid and not through
 * the row a cell happens to sit in.
 */
function inRows(
	entries: readonly EmojiEntry[],
	columns: number,
): { start: number; row: EmojiEntry[] }[] {
	const rows: { start: number; row: EmojiEntry[] }[] = []
	for (let start = 0; start < entries.length; start += columns) {
		rows.push({ start, row: [...entries.slice(start, start + columns)] })
	}
	return rows
}

interface GridProps {
	label: string
	entries: readonly EmojiEntry[]
	/** How many cells a row holds — the panel's own count, so the keys and the grid agree. */
	columns: number
	tone: number
	onPick: (char: string) => void
	/** Set on the grid the search box's ArrowDown enters — the first one on the panel. */
	entry?: React.RefObject<HTMLDivElement | null>
}

/**
 * One grid of emoji as one composite widget: a single tab stop, and the keys move inside it. The
 * handler sits on the buttons rather than on the container, because the keys belong to the roving
 * stops and not to the box that holds them — the same shape the message list uses.
 *
 * The caller gives each grid a `key` of its own, so choosing another category or typing in the
 * search box mounts a new one and the roving stop starts at its first cell rather than pointing
 * past the end of a shorter list.
 */
function EmojiGrid({ label, entries, columns, tone, onPick, entry }: GridProps) {
	const own = useRef<HTMLDivElement>(null)
	const ref = entry ?? own
	const [active, setActive] = useState(0)

	const onKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLButtonElement>) => {
			if (!OWN_KEYS.has(event.key)) return
			event.preventDefault()
			event.stopPropagation()
			const next = gridTarget(event, active, entries.length, columns)
			if (next === null) return
			setActive(next)
			buttonsIn(ref.current)[next]?.focus()
		},
		[active, columns, entries.length, ref],
	)

	if (entries.length === 0) {
		return <p className="tb-emoji-picker__empty">{t("emoji.noHits")}</p>
	}

	return (
		<div className="tb-emoji-picker__grid" ref={ref} role="grid" aria-label={label}>
			{inRows(entries, columns).map(({ start, row }) => (
				<div className="tb-emoji-picker__row" role="row" key={row[0]?.char ?? ""}>
					{row.map((emoji, column) => {
						const at = start + column
						const char = withSkinTone(emoji, tone)
						return (
							<div className="tb-emoji-picker__cell" role="gridcell" key={emoji.char}>
								<button
									type="button"
									className="tb-emoji-picker__emoji"
									data-emoji={char}
									aria-label={emoji.label}
									tabIndex={at === active ? 0 : -1}
									onKeyDown={onKeyDown}
									onFocus={() => setActive(at)}
									onClick={() => onPick(char)}
								>
									{char}
								</button>
							</div>
						)
					})}
				</div>
			))}
		</div>
	)
}

interface StripProps {
	/** Tabs choose what the panel below them shows; a toolbar's buttons change a setting. */
	kind: "tabs" | "toolbar"
	label: string
	className: string
	itemClassName: string
	items: { key: string; label: string; glyph: string }[]
	selected: number
	onSelect: (index: number) => void
	/** The panel the tabs control; a toolbar controls nothing. */
	controls?: string
}

/**
 * A strip of buttons that is one tab stop: the arrows walk it and choose as they go (the APG's
 * automatic activation), Home and End jump to its ends. The category tabs and the tone swatches
 * are the same widget — only the role and the way "chosen" is spelt differ.
 *
 * Both wear the shared track (`.tb-tabs`), so a chosen category is lifted onto the raised ground
 * with the accent's ink like every other segmented control in the product instead of carrying an
 * underline of its own. The items are plain buttons rather than `Button`s: `.tb-tabs__item` *is* the
 * control's paint, and two paints on one element is how a track ends up with a button inside it.
 */
function Strip({
	kind,
	label,
	className,
	itemClassName,
	items,
	selected,
	onSelect,
	controls,
}: StripProps) {
	const ref = useRef<HTMLDivElement>(null)

	const onKeyDown = (event: React.KeyboardEvent) => {
		if (!OWN_KEYS.has(event.key)) return
		event.preventDefault()
		event.stopPropagation()
		const last = items.length - 1
		const next =
			event.key === "ArrowLeft" || event.key === "ArrowUp"
				? Math.max(0, selected - 1)
				: event.key === "ArrowRight" || event.key === "ArrowDown"
					? Math.min(last, selected + 1)
					: event.key === "Home"
						? 0
						: event.key === "End"
							? last
							: null
		if (next === null) return
		onSelect(next)
		// The stop moves with the choice, so the focus follows it in the same gesture.
		buttonsIn(ref.current)[next]?.focus()
	}

	return (
		<div
			className={className}
			ref={ref}
			role={kind === "tabs" ? "tablist" : "toolbar"}
			aria-label={label}
		>
			{items.map((item, index) => (
				<button
					key={item.key}
					type="button"
					className={itemClassName}
					role={kind === "tabs" ? "tab" : undefined}
					aria-label={item.label}
					aria-controls={kind === "tabs" ? controls : undefined}
					{...(kind === "tabs"
						? { "aria-selected": index === selected }
						: { "aria-pressed": index === selected })}
					tabIndex={index === selected ? 0 : -1}
					onKeyDown={onKeyDown}
					onClick={() => onSelect(index)}
				>
					{item.glyph}
				</button>
			))}
		</div>
	)
}

export interface EmojiPickerProps {
	/** The control the panel hangs from, and where Escape puts the focus back. */
	trigger: ReactNode
	/** The panel's accessible name, already translated. */
	label: string
	/** The chosen sequence, at the person's tone; the panel closes itself around it. */
	onPick: (char: string) => void
	/**
	 * Present: the caller owns the open state, because something other than the trigger opens the
	 * panel — the message row's menu choosing *Add a reaction* while the panel hangs from the hover
	 * bar's button.
	 */
	open?: boolean
	onOpenChange?: (open: boolean) => void
}

/**
 * The picker as a surface uses it: a trigger and the panel that hangs from it.
 *
 * The open state is the primitive's own until a caller claims it, and a pick closes the panel either
 * way — the one thing the old shape could not do, which is why every call site used to remount the
 * panel under a new key to get rid of it.
 */
export function EmojiPicker({ trigger, label, onPick, open, onOpenChange }: EmojiPickerProps) {
	const [ownOpen, setOwnOpen] = useState(false)
	const owned = open === undefined
	const change = useCallback(
		(next: boolean) => {
			if (owned) setOwnOpen(next)
			onOpenChange?.(next)
		},
		[owned, onOpenChange],
	)
	return (
		<Popover trigger={trigger} label={label} open={open ?? ownOpen} onOpenChange={change}>
			<EmojiPanel
				onPick={(char) => {
					onPick(char)
					change(false)
				}}
			/>
		</Popover>
	)
}

/**
 * The panel's own shapes while the vocabulary is on its way: the category strip and a grid of the
 * final size, drawn as the product's skeleton band (`ui.css`'s `.tb-state__line`, the one shimmer in
 * the interface). They are `aria-hidden` and hold nothing a reader could operate — the field and the
 * tone row beside them are real from the first frame — so the panel is the right size and the right
 * shape at once and nothing jumps when the table lands (X-B3 D5).
 */
function PanelSkeleton({ columns }: { columns: number }) {
	return (
		<>
			<div className="tb-tabs tb-tabs--scroll tb-emoji-picker__tabs" aria-hidden="true">
				{Object.keys(GROUP_ICONS).map((key) => (
					<span key={key} className="tb-state__line tb-emoji-picker__ghost" />
				))}
			</div>
			<span className="tb-state__line tb-emoji-picker__ghost" aria-hidden="true" />
			<div className="tb-emoji-picker__grid" aria-hidden="true">
				{Array.from({ length: ROWS_VISIBLE }, (_, row) => (
					<div className="tb-emoji-picker__row" key={row}>
						{Array.from({ length: columns }, (_, cell) => (
							<div className="tb-emoji-picker__cell" key={cell}>
								<span className="tb-state__line tb-emoji-picker__ghost" />
							</div>
						))}
					</div>
				))}
			</div>
		</>
	)
}

/** The panel itself: the field, the category track, the grids and the tone row. */
function EmojiPanel({ onPick }: { onPick: (char: string) => void }) {
	const table = useEmojiTable()
	const prefs = useEmojiPrefs()
	const [term, setTerm] = useState("")
	const [groupKey, setGroupKey] = useState("")
	const search = useRef<HTMLInputElement>(null)
	const firstGrid = useRef<HTMLDivElement>(null)
	const panelId = `${useId()}category`
	// Read rather than subscribed to, exactly as the composer reads its pointer: a pointer type does
	// not change under a person's hands, and a grid that re-flowed mid-gesture would move the cell a
	// thumb is over.
	const columns = gridColumns()

	const groups = table?.groups ?? []
	const found = groups.findIndex((candidate) => candidate.key === groupKey)
	const selected = found === -1 ? 0 : found
	const group = groups[selected]

	const recents = useMemo(() => {
		if (!table) return []
		return prefs.recents
			.map((char) => table.byChar(char))
			.filter((entry): entry is EmojiEntry => entry !== undefined)
	}, [table, prefs.recents])

	const results = useMemo(
		() => (table && term.trim() !== "" ? table.search(term, SEARCH_LIMIT) : null),
		[table, term],
	)

	const pick = useCallback(
		(char: string) => {
			rememberEmoji(char)
			onPick(char)
		},
		[onPick],
	)

	// The panel opens with the focus in the search box, which exists from the first frame: typing
	// already works there while the table is still arriving, and both the grid and the strips are one
	// key away from it (WCAG 2.4.3).
	useEffect(() => {
		search.current?.focus()
	}, [])

	/** ArrowDown out of the field is how the keyboard reaches the emoji themselves. */
	const onSearchKeyDown = (event: React.KeyboardEvent) => {
		if (!OWN_KEYS.has(event.key)) return
		// Home, End and the sideways arrows belong to the caret in this field, and nothing outside.
		event.stopPropagation()
		if (event.key !== "ArrowDown") return
		event.preventDefault()
		buttonsIn(firstGrid.current)
			.find((button) => button.tabIndex === 0)
			?.focus()
	}

	return (
		<div
			className="tb-emoji-picker"
			style={{ "--emoji-columns": columns } as CSSProperties}
			// While the vocabulary is on its way the panel is its own final shape with nothing in it;
			// `aria-busy` is what says so, and the sentence below is what a screen reader hears.
			aria-busy={table === undefined}
		>
			<input
				type="search"
				className="tb-field__control tb-emoji-picker__search"
				ref={search}
				aria-label={t("emoji.search")}
				placeholder={t("emoji.search")}
				value={term}
				onChange={(event) => setTerm(event.target.value)}
				onKeyDown={onSearchKeyDown}
			/>

			{table === undefined ? (
				<>
					<span className="tb-visually-hidden" role="status">
						{t("emoji.loading")}
					</span>
					<PanelSkeleton columns={columns} />
				</>
			) : null}

			{table !== undefined && results === null ? (
				<Strip
					kind="tabs"
					label={t("emoji.categories")}
					className="tb-tabs tb-tabs--scroll tb-emoji-picker__tabs"
					itemClassName="tb-tabs__item tb-emoji-picker__tab"
					items={groups.map((candidate) => ({
						key: candidate.key,
						label: groupLabel(candidate),
						glyph: GROUP_ICONS[candidate.key] ?? candidate.key.slice(0, 2),
					}))}
					selected={selected}
					onSelect={(index) => setGroupKey(groups[index]?.key ?? "")}
					controls={panelId}
				/>
			) : null}

			{table !== undefined && results === null && recents.length > 0 ? (
				<>
					<h3 className="tb-emoji-picker__heading">{t("emoji.recent")}</h3>
					<EmojiGrid
						key="recent"
						label={t("emoji.recent")}
						entries={recents}
						columns={columns}
						tone={prefs.skinTone}
						onPick={pick}
						entry={firstGrid}
					/>
				</>
			) : null}

			{table !== undefined && results === null && group ? (
				<div role="tabpanel" id={panelId} aria-label={groupLabel(group)}>
					<h3 className="tb-emoji-picker__heading">{groupLabel(group)}</h3>
					<EmojiGrid
						key={group.key}
						label={groupLabel(group)}
						entries={group.entries}
						columns={columns}
						tone={prefs.skinTone}
						onPick={pick}
						{...(recents.length === 0 ? { entry: firstGrid } : {})}
					/>
				</div>
			) : null}

			{results === null ? null : (
				<EmojiGrid
					key={`search:${term}`}
					label={t("emoji.results")}
					entries={results}
					columns={columns}
					tone={prefs.skinTone}
					onPick={pick}
					entry={firstGrid}
				/>
			)}

			<Strip
				kind="toolbar"
				label={t("emoji.skinTone")}
				className="tb-tabs tb-tabs--scroll tb-emoji-picker__tones"
				itemClassName="tb-tabs__item tb-emoji-picker__tone"
				items={TONES.slice(0, MAX_SKIN_TONE + 1).map((tone) => ({
					key: tone.key,
					label: t(tone.key),
					glyph: tone.swatch,
				}))}
				selected={prefs.skinTone}
				onSelect={setSkinTone}
			/>
		</div>
	)
}
